
import Vue from 'vue';
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import NavProfile from './NavProfile.vue';
import NavHamburger from './NavHamburger.vue';
import NavContent from '@/components/NavigationBar/NavContent.vue';
import SkadeLogoWithText from '~/assets/icons/skade-logo-with-text.svg';
import MoreIcon from '../ui/icons/MoreIcon.vue';
import HelpIcon from '../ui/icons/HelpIcon.vue';
import LogoIcon from '~/assets/icons/skade_logo.svg';
import { getWindowInstance } from '~/plugins/windowInstance';
import { appQueryParams, appURLs } from '@/utils/route-redirects';
import { unlocalizeRouteName } from '~/utils/routeNames';
import { UIMenuMode } from '~/store/modules/ui-messaging/types';
import { eventManager } from '@/utils/behavior/applicationEvents/event-manager';
import { capitalizeFirstLetter } from '@/utils/string-formatter';
import { inAppRedirect } from '~/utils/route-redirects';

enum NavigationMode {
  Standard = 'standard',
  Titular = 'titular',
}

// Explicitly typed because all routes are currently commented out, causing TypeScript to infer {} and reject string indexing
const helpButtonVisibility: Partial<Record<string, 'all' | 'mobile'>> = {
  // NOTE- #SKD-2226 - We will not be displaying the help button since we've removed the tooltips. Keeping the logic here in case we want to add it back later on
  // [appURLs.feed()]: 'all',
  // NOTE - #SKD-2184 - Disabling the portfolio help center button for now until the guides get updated
  // [appURLs.portfolio()]: 'all',
};

export default Vue.extend({
  components: {
    SkadeLogoWithText,
    LogoIcon,
    NavContent,
    NavHamburger,
    NavProfile,
    // Sidebar,
    MoreIcon,
    HelpIcon,
  },
  computed: {
    feedUrl(): string {
      return this.localePath(appURLs.feed());
    },
    isMobileResponsive(): boolean {
      // @ts-ignore TODO fix typescript not recognizing this
      return getWindowInstance(this).isMobileResponsive();
    },
    showingMobileSearchPopup(): boolean {
      // @ts-ignore TODO fix typescript not recognizing this
      return this.isMobileResponsive && this.searchPopupShown;
    },
    ...mapGetters('search', ['searchPopupShown']),
    ...mapGetters('modals', ['helpCenterObjectRes']),
    ...mapGetters('authentication', ['profile']),
    ...mapGetters('ui-messaging', [
      'topNavigationContentMode',
      'topNavigationProfile',
      'topNavigationLeftAction',
      'customBackRoute',
    ]),
    ...mapGetters('profile', { profileNavigatedTo: 'profile' }),
    ...mapState('ui-messaging', ['pageNested', 'pageTitle', 'pageMenuMode']),
    /**
     * The current navigation mode.
     * If the current navigation mode is standard, then a standard search bar
     * will be shown, but `nested` displays a titular page with a back icon
     * emitting a back event.
     */
    mode(): NavigationMode {
      return this.pageNested && ['sm'].includes(this.$mq)
        ? NavigationMode.Titular
        : NavigationMode.Standard;
    },
    /**
     * The overal navigation bar mode for supporting titular pages such as
     * "Settings".
     */
    contentMode(): string {
      // TODO document this
      if (
        this.isMobileResponsive &&
        this.topNavigationContentMode === 'profile'
      )
        return 'profile';

      const routeName = (this.$route?.name ?? '').split('_')[0];

      if (
        ['sm'].includes(this.$mq) &&
        ['portfolios-portfolioId'].indexOf(routeName) != -1
      ) {
        return 'portfolio';
      }

      return this.mode === 'titular' ? 'title' : 'search';
    },
    /** Which mode the left menu should be in. */
    menuMode(): string {
      if (!this.isMobileResponsive) {
        return UIMenuMode.Empty;
      }
      // If the search popup is shown and the user is on mobile, use empty content mode.
      if (this.isMobileResponsive && this.searchPopupShown)
        return UIMenuMode.Empty;

      // If on mobile and the top navigation left action is set to back, allow the user to use the back button.
      if (this.isMobileResponsive && this.topNavigationLeftAction === 'back')
        return 'back';

      // Special cases handling
      if ('follows' in this.$route.query) {
        return 'back';
      }
      // Regular use cases using the store
      // @ts-ignore TODO fix typescript error
      switch (this.pageMenuMode) {
        case 'burgerMenu':
          return 'menu';
        //@ts-ignore fix this TS issue
        case 'backArrow':
          // do not show the back arrow on non-mobile screen sizes
          return this.isMobileResponsive ? 'back' : 'menu';
        case 'empty':
          return 'empty';
        default:
          return 'menu';
      }
    },
    /**
     *  Whether to show the help center button in the top nav.
     * As per Manon's request, the help center button in the top nav should be shown whenever the search bar itself is shown.
     * This means that this property should be kept in sync with the condition used in NavContent to display the Searchbar
     * components/NavigationBar/NavContent.vue:9
     */
    showHelpCenter(): boolean {
      const visibility = helpButtonVisibility[this.route];
      if (!visibility || this.contentMode !== 'search') return false;
      return (
        visibility === 'all' ||
        (visibility === 'mobile' && this.isMobileResponsive)
      );
    },
    showSendMessageButton(): object {
      const route = this.$route;
      const routeName = route?.name ? route.name.split('_')[0] : '';

      return {
        leftButton: routeName === appURLs.feed() && this.isMobileResponsive,
        rightButton:
          this.isMobileResponsive &&
          routeName.includes(appURLs.profile()) &&
          this.profileNavigatedTo?.profileId === this.profile.profileId,
      };
    },
    showLogoMobile(): boolean {
      return (
        this.contentMode === 'search' &&
        this.isMobileResponsive &&
        !this.searchPopupShown
      );
    },
    showMoreIcon(): boolean {
      const route = this.$route;
      const routeName = route?.name ? route.name.split('_')[0] : '';

      return (
        (routeName === appURLs.notification_center() ||
          routeName === appURLs.portfolio()) &&
        this.isMobileResponsive &&
        !this.searchPopupShown
      );
    },
    showPortfolioHelpIcon(): boolean {
      // Node - #SKD-2184 - Disabling portfolio help icon for now until the guides get updated
      return false;
      // const route = this.$route;
      // const routeName = route?.name ? route.name.split('_')[0] : '';

      // return routeName === appURLs.portfolio() && this.isMobileResponsive;
    },
    route(): string {
      const route = this.$route;
      const routeName = route?.name ? route.name.split('_')[0] : '';

      return routeName;
    },
    /** Whether to show the logo next to the hamburger (homepage, mobile only). */
    showHomeLogo(): boolean {
      return this.isMobileResponsive && this.route === appURLs.feed();
    },
  },
  watch: {
    $route() {
      // make the search popup not shown when navigation occurs
      if (!this.isMobileResponsive) {
        this.handleSearchPopupShown(false);
      }
    },
  },
  methods: {
    ...mapActions('search', ['showSearchPopup', 'hideSearchPopup']),
    ...mapActions('modals', [
      'toggleNotificationsCenterDrawerMenu',
      'togglePortfolioOptionsDrawer',
      'togglePortfolioGuideDrawer',
      'togglePortfolioGuideModal',
      'toggleHomePageGuideDrawer',
      'toggleMobileSidebar',
    ]),
    ...mapActions('profile', ['setContentStep']),
    ...mapActions('onboarding', ['toggleOnboardingTooltips']),
    // TODO we should not be using mutations directly
    ...mapMutations('ui-messaging', [
      'TRIGGER_CUSTOM_BACK_ACTION',
      'SET_CUSTOM_BACK_ROUTE',
    ]),
    hamburgerBackClicked(): void {
      if (this.isMobileResponsive && this.searchPopupShown) {
        this.handleSearchPopupShown(false);
        this.$router.go(-1);
        return;
      }

      if ('follows' in this.$route.query) {
        // Updating query here because if we keep 'follows' param in the query we would have
        // back arrow displayed and it does nothing on click
        const newQuery = { ...this.$route.query };

        delete newQuery.follows;

        this.$router.push({ query: newQuery });
        this.setContentStep(0);
      }

      // TODO document
      if (appQueryParams.sideBarMenu in this.$route.query) {
        this.$router.go(-1);
        return;
      }

      if (
        this.$route &&
        (this.$route.name?.includes('settings') ||
          [appURLs.messages()].includes(
            // @ts-ignore TODO fix this TS error
            unlocalizeRouteName(this.$route)
          ))
      ) {
        // TODO replace this with an action
        // TODO add this mutation to the mixin
        this.TRIGGER_CUSTOM_BACK_ACTION(true);
        return;
      }

      if (this.customBackRoute) {
        inAppRedirect(this.customBackRoute);
        this.SET_CUSTOM_BACK_ROUTE(null);
      } else {
        this.$router.go(-1);
      }
    },
    openHelpCenter(): void {
      // NOTE - #SKD-2184 - Disabling the portfolio help center button for now until the guides get updated
      // if (this.route === appURLs.portfolio()) {
      //   this.togglePortfolioGuideModal(true);
      // }

      if (this.route === appURLs.index()) {
        this.toggleOnboardingTooltips(true);
      }
    },
    handleSearchPopupShown(searchPopupShown: boolean): void {
      if (searchPopupShown) this.showSearchPopup();
      else this.hideSearchPopup();
    },
    handleMoreIconClick(shouldShow: boolean): void {
      // Probably not the best way to implement this but since we are now using this icon on multiple pages
      // we'll use the route to determine which action we want to make on icon click
      const route = this.$route;
      const routeName = route?.name ? route.name.split('_')[0] : '';

      if (routeName === appURLs.notification_center()) {
        this.raiseClickMenuPrincipalEvent('NotificationsMoreIcon');
        this.toggleNotificationsCenterDrawerMenu(shouldShow);
      }

      if (routeName === appURLs.portfolio()) {
        this.raiseClickMenuPrincipalEvent('PortfolioMoreIcon');

        this.togglePortfolioOptionsDrawer(shouldShow);
      }
    },
    // NOTE - #SKD-2184 - Removing the button calling this, to enable back when the guides get updated
    handlePortfolioHelpIconClick(shouldShow: boolean): void {
      this.raiseClickMenuPrincipalEvent('Portfolio Help');
      this.togglePortfolioGuideDrawer(shouldShow);
    },
    handleGoToFollowers() {
      this.setContentStep(1);
    },
    handleGoToFollowed() {
      this.setContentStep(2);
    },
    handleBurgerMenuClicked() {
      this.toggleMobileSidebar();
      eventManager.raiseToggleMenuBurgerEvent({ state: 'Open' });
    },
    raiseClickMenuPrincipalEvent(value: string) {
      eventManager.raiseClickMenuPrincipalEvent({
        text: 'TopNavbar | ' + capitalizeFirstLetter(value),
      });
    },
  },
});
